import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { updateProfile, updateSelf } from "../../../../../api/UserAPI";
import { useProfileStore } from "../../../../../stores/UserStore";
import {
  Button,
  Divider,
  Group,
  LoadingOverlay,
  NumberInput,
  Stack,
  Text,
} from "@mantine/core";
import BackButton from "../../../../../components/View/BackButton";
import ScoreLinkSpecific from "./ScoreLinkSpecific";
import { notifications } from "@mantine/notifications";

const ScoreContent = ({ handleClose }) => {
  const lang = useLanguage((s) => s.language);
  const [loading, setLoading] = useState(true);
  const profile = useProfileStore((s) => s.profile);
  const [submitting, setSubmitting] = useState(false);
  const [links, setLinks] = useState(
    profile?.prm_scoring?.link_click_specific_link
      ? Object.keys(profile?.prm_scoring?.link_click_specific_link)?.map(
          (key, i) => ({
            key,
            value: profile?.prm_scoring?.link_click_specific_link[key],
            id: i,
          })
        )
      : [
          {
            key: "",
            value: null,
            id: 0,
          },
        ]
  );

  const defaultScore = useMemo(() => {
    if (profile?.prm_scoring) return profile.prm_scoring;
    return {
      link_click: 10,
      open: 10,
      answer: 10,
      positive_answer: 50,
      negative_answer: -10,
      invitation_accepted: 30,
    };
  }, [profile]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    let newData = {};
    Object.keys(data).forEach((k) => {
      newData[k] = parseInt(data[k]);
    });

    let link_click_specific_link = {};
    links.forEach((link) => {
      if (link.key && link.value) {
        link_click_specific_link[link.key] = link.value;
      }
    });
    newData.link_click_specific_link =
      Object.keys(link_click_specific_link).length === 0
        ? null
        : link_click_specific_link;

    setSubmitting(true);
    updateProfile(null, { prm_scoring: newData })
      .then((response) => {
        updateSelf();
        notifications.show({
          message: lang.prm.scoring.saved,
          color: "green.8",
        });
        handleClose();
      })
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    if (loading) {
      updateSelf().finally(() => {
        setLoading(false);
      });
    }
  }, [loading]);

  return (
    <>
      <Stack
        pos={"relative"}
        gap={"xs"}
        h={"100%"}
        component="form"
        onSubmit={handleSubmit}
        id="form"
      >
        <LoadingOverlay visible={loading} />

        <Text c={"dimmed"}>{lang.prm.scoring.text}</Text>

        <Group justify="space-between">
          {Object.keys(defaultScore).map(
            (key) =>
              key !== "link_click_specific_link" && (
                <NumberInput
                  key={key}
                  name={key}
                  label={lang.prm.scoring.list?.[key] || "???"}
                  defaultValue={defaultScore?.[key] || 0}
                  placeholder={lang.prm.scoring.list?.[key]}
                  w={"30%"}
                  size="md"
                  required={key === "link_click" && links.length > 0}
                  min={-32767}
                  max={32767}
                />
              )
          )}
        </Group>

        <Divider
          label={lang.prm.scoring.specific_link_divider}
          styles={{ label: { fontSize: 12 } }}
          mt={8}
        />
        <Text>{lang.prm.scoring.specific_link_text}</Text>
        <ScoreLinkSpecific links={links} setLinks={setLinks} />
      </Stack>

      <Group justify="end" mt={"xl"}>
        <BackButton onClick={handleClose} size="md" />
        <Button loading={submitting} form="form" type="submit" size="md">
          {lang.global.save}
        </Button>
      </Group>
    </>
  );
};

export default ScoreContent;
